import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as L from 'leaflet';
import '../../lib/leaflet-active-area';

// import { baseURL } from '../common/config';
import ResetLayers from './ResetLayers';

// set default image paths for Leaflet
// note the "gin-static-site-starter" as the first directory if NODE_ENV === production
// this is because Github Pages will be looking for the markers at:
// https://greeninfo-network.github.io/gin-static-site-starter/assets/icons/<filename>.png
// while locally it will be localhost:8080/assets/icons/<filename>.png
L.Icon.Default.imagePath = '../../';
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'assets/icons/marker-icon-2x.png',
  iconUrl: 'assets/icons/marker-icon.png',
  shadowUrl: 'assets/icons/marker-shadow.png',
});

class LeafletMap extends Component {
  static propTypes = {
    appWidth: PropTypes.number.isRequired,
    activeLayers: PropTypes.arrayOf(PropTypes.string).isRequired,
    basemap: PropTypes.string.isRequired,
    deselectAllMapLayers: PropTypes.func.isRequired,
    fetchGeoJsonLayer: PropTypes.func.isRequired,
    isFetchingGeoJson: PropTypes.bool.isRequired,
    // eslint-disable-next-line
    dataLayers: PropTypes.object.isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    zoom: PropTypes.number.isRequired,
    zoomLayer: PropTypes.string,
    zoomToLayerExtent: PropTypes.func.isRequired,
    openLightbox: PropTypes.func.isRequired,
  };

  static defaultProps = {
    zoomLayer: '',
  };

  constructor(props) {
    super(props);
    const { zoom } = props;

    const basemapsConfig = [
      {
        attrib: 'CARTO, OSM',
        label: 'Detail Map',
        tooltip: 'Detail Map',
        type: 'xyz',
        url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}@2x.png',
      },
      {
        attrib: 'Stamen, OSM',
        label: 'Watercolor',
        tooltip: 'Watercolor',
        type: 'xyz',
        url: 'https://tiles.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg',
      },
    ];

    // custom marker base class for warning icon
    const WarningIcon = L.Icon.extend({
      options: {
        iconUrl: './assets/icons/warning_icon.png',
        iconRetinaUrl: './assets/icons/warning_icon@2x.png',
        shadowUrl: './assets/icons/warning_icon_shadow.png',
        iconSize: [39, 37],
        shadowSize: [39, 37],
        iconAnchor: [19, 18],
        shadowAnchor: [19, 18],
        // popupAnchor: [2, -40]
      }
    });

    this.warning_icon = new WarningIcon();

    this.mapOptions = {
      minZoom: zoom,
      maxZoom: 7,
      inertia: false,
      attributionControl: false,
      zoomControl: false,
    };

    // to store geoJson layers
    this.vectorLayers = null;
    // clear selected layers in map reducer
    this.clearSelectedLayers = this.clearSelectedLayers.bind(this);

    // basemap tile layers that can be toggled
    this.watercolor = L.tileLayer(basemapsConfig[1].url, {
      attribution: basemapsConfig[1].attrib,
      zIndex: 0,
    });
    this.detailmap = L.tileLayer(basemapsConfig[0].url, {
      attribution: basemapsConfig[0].attrib,
      zIndex: 0,
    });
  }

  componentDidMount() {
    this.initMap();
  }

  componentWillReceiveProps(nextProps) {
    const { activeLayers, basemap, dataLayers, isFetchingGeoJson, zoomLayer } = nextProps;

    // if there are more or less items in the activeLayers array, toggle map layers
    if (activeLayers.length !== this.props.activeLayers.length) {
      this.handleLayerToggle(dataLayers);
    }

    // also call handleLayerToggle if data for a GeoJSON layer was successfully fetched
    if (!isFetchingGeoJson && this.props.isFetchingGeoJson) {
      this.handleLayerToggle(dataLayers);
    }

    // if the datacode for the zoomLayer updates, update the map view
    if (zoomLayer !== '') {
      this.zoomToMapLayer(zoomLayer);
    }

    // if the basemap is toggled
    if (basemap !== this.props.basemap) {
      this.toggleBasemapLayer(basemap);
    }
  }

  shouldComponentUpdate() {
    // Let Leaflet handle this part of the DOM, not React!
    return false;
  }

  clearSelectedLayers() {
    this.props.deselectAllMapLayers();
  }

  zoomToMapLayer(layerCode) {
    const { dataLayers, zoom, lat, lng, zoomToLayerExtent } = this.props;
    const vectorLayers = this.vectorLayers;
    const layer = dataLayers[layerCode];

    // if the layer has not yet been added or is not active, do nothing
    if (!layer.selected) return;
    if (layer.datatype === 'geojson' && !vectorLayers.hasLayer(layer.leaflet._leaflet_id)) {
      return;
    }

    const datatype = layer.datatype;

    if (datatype === 'geojson') {
      this.map.fitBounds(layer.leaflet.getBounds());
    }

    if (datatype === 'tileLayer') {
      // TO DO: use a bounding box property of the dataLayer?
      this.map.setView([lat, lng], zoom);
    }

    // fire the zoomToLayerExtent action again, so that it may be called for the same layer
    // over and over, e.g. after it was called the user pans / zooms, then calls it again
    zoomToLayerExtent('');
  }

  toggleGeoJsonLayer(layer) {
    // add or remove a geojson layer from vectorLayers feature group
    const vectorLayers = this.vectorLayers;

    // if the layer is not selected, has an L.geoJson class, and is present in the map layer group,
    // remove it
    if (!layer.selected && layer.leaflet && vectorLayers.hasLayer(layer.leaflet._leaflet_id)) {
      vectorLayers.removeLayer(layer.leaflet);
    }

    // if we don't have the GeoJSON layer yet, make an HTTP Request for it
    if (layer.selected && !layer.leaflet) {
      this.props.fetchGeoJsonLayer(layer.datacode);
    }

    // if we've already fetched geojson data and created the leaflet layer
    // then add it to the layers group
    if (layer.selected && layer.leaflet) {
      vectorLayers.addLayer(layer.leaflet.setStyle(layer.style));
    }
  }

  toggleTileLayer(layer) {
    // add or remove a tileLayer from the map
    if (!layer.selected && this.map.hasLayer(layer.leaflet)) {
      this.map.removeLayer(layer.leaflet);
    }

    if (layer.selected && !this.map.hasLayer(layer.leaflet)) {
      if (layer.tile_opacity !== null) {
        layer.leaflet.setOpacity(layer.tile_opacity);
      }
      this.map.addLayer(layer.leaflet);
    }
  }

  toggleMarkerLayer(layer) {
    const { openLightbox } = this.props;

    const name = layer.layerlabel;
    const description = layer.description;
    const shortdescription = layer.shortdescription;
    const source = layer.source;
    const sourceurl = layer.sourceurl;
    const fullcitation = layer.fullcitation;

    // data that gets passed to the lightbox
    const payload = {
      name,
      description,
      shortdescription,
      source,
      sourceurl,
      fullcitation,
    };

    // add or remove a tileLayer from the map
    if (!layer.selected && this.map.hasLayer(layer.leaflet)) {
      this.map.removeLayer(layer.leaflet);
    }

    if (layer.selected && !this.map.hasLayer(layer.leaflet)) {
      // hack
      layer.leaflet.setIcon(this.warning_icon);
      layer.leaflet.on('click', () => openLightbox('dataLayer', payload));
      this.map.addLayer(layer.leaflet);
    }
  }

  toggleBasemapLayer(basemap) {
    if (basemap === 'watercolor' && !this.map.hasLayer(this.watercolor)) {
      this.map.removeLayer(this.detailmap);
      this.map.addLayer(this.watercolor);
    }
    if (basemap === 'detailmap' && !this.map.hasLayer(this.detailmap)) {
      this.map.removeLayer(this.watercolor);
      this.map.addLayer(this.detailmap);
    }
  }

  handleLayerToggle(dataLayers) {
    Object.keys(dataLayers).forEach(layer => {
      const o = dataLayers[layer];

      if (o.datatype === 'geojson') {
        this.toggleGeoJsonLayer(o);
      }

      if (o.datatype === 'tileLayer') {
        this.toggleTileLayer(o);
      }

      if (o.datatype === 'marker') {
        this.toggleMarkerLayer(o);
      }

    });
  }

  initMap() {
    // map setup
    const { dataLayers, lat, lng, zoom } = this.props;

    this.map = new L.Map('map', this.mapOptions)
      .setActiveArea('active-area')
      .setView([lat, lng], zoom);

    this.map.scrollWheelZoom.disable();

    this.watercolor.addTo(this.map);

    this.vectorLayers = L.featureGroup().addTo(this.map);

    L.control.zoom({ position: 'topright' }).addTo(this.map);

    L.control.scale({ position: 'bottomright' }).addTo(this.map);

    // make sure any default data layers are enabled
    this.handleLayerToggle(dataLayers);

    window.map = this.map;
  }

  render() {
    // NOTE: Because shouldComponentUpdate always returns false, Render is only called once when the app loads
    return (
      <div id="map">
        <ResetLayers handleClick={this.clearSelectedLayers} />
        <div className="active-area" />
      </div>
    );
  }
}

export default LeafletMap;



// WEBPACK FOOTER //
// ./src/components/LeafletMap.jsx